<template>
  <main v-loading="loading" class="rebate-tab">
    <div v-if="!showPage" style="height: 300px" />
    <template v-if="showPage && !registered">
      <p>{{ $t('member.tab.point.notRegistered') }}</p>
    </template>
    <FlexLayout v-if="showPage && registered" col :gap="20">
      <FlexLayout v-if="showControl.operation" col>
        <PointStatistic
          :memberId="memberId"
          :loading="loading"
          :shopPointKey="shopPointKey"
          :pointBalance="pointWalletBalance"
        />
        <UsePointBlock
          v-if="showControl.operation"
          :memberId="memberId"
          :pointBalance="pointWalletBalance"
          :shopPointKey="shopPointKey"
          :pointFeatrue="pointFeatrue"
          @refresh="refresh"
        />
      </FlexLayout>
      <FlexLayout v-if="showControl.record" col :gap="20">
        <BasicTabs v-model="activeSubTab" :tabs="subTabOptions" />
        <PointBalanceDetail
          v-if="activeSubTab === 'balance'"
          ref="balanceDetailRef"
          :memberId="memberId"
          :shopPointKey="shopPointKey"
        />
        <PointRecordList
          v-else
          ref="recordListRef"
          :memberId="memberId"
          :shopPointKey="shopPointKey"
          :isExternalPoint="isExternalPoint"
        />
      </FlexLayout>
    </FlexLayout>
  </main>
</template>

<script>
import { FindShopPoint } from '@/api/shopPoint'
import PointStatistic from './components/PointStatistic.vue'
import UsePointBlock from './components/UsePointBlock.vue'
import PointBalanceDetail from './components/PointBalanceDetail.vue'
import PointRecordList from './components/PointRecordList.vue'
import store from '@/store'
import { computed, ref, onMounted, defineComponent, provide, nextTick } from 'vue'
import { get, find } from 'lodash'
import { usePermissions } from '@/use/permissions'
import { useShop } from '@/use/shop'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  components: {
    PointStatistic,
    UsePointBlock,
    PointBalanceDetail,
    PointRecordList,
  },
  props: {
    memberId: {
      type: String,
    },
    pointWalletBalance: {
      type: Object,
      default: () => ({}),
    },
    shopPointKey: {
      type: String,
      required: true,
    },
    pointFeatrue: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const { shopPointList } = useShop()
    const loading = ref(false)
    const showPage = ref(false)
    const registered = ref(false)
    const shopId = computed(() => store.getters.shop)
    const { checkAction } = usePermissions()
    const activeSubTab = ref('record')
    const balanceDetailRef = ref(null)
    const recordListRef = ref(null)

    const subTabOptions = computed(() => {
      const options = []
      if (showControl.value.balance) {
        options.push({ value: 'balance', label: i18n.t('member.tab.point.balanceDetail') })
      }
      options.push({ value: 'record', label: i18n.t('member.tab.point.usageRecord') })
      return options
    })

    // ?? 是否為第三方點數（使用外部點數紀錄 API）
    const isExternalPoint = computed(() => {
      return get(props.pointFeatrue, 'findExternalPointWalletRecord', false)
    })

    // ?? 當前頁面的點數
    const pageShopPoint = computed(() => {
      const pointData = find(shopPointList.value, { key: props.shopPointKey }) || find(shopPointList.value, { isDefault: true })
      return pointData || { name: i18n.t('member.tab.point.defaultName'), unitName: i18n.t('member.tab.point.defaultUnit') }
    })

    const showControl = computed(() => ({
      operation: (get(props.pointFeatrue, 'addPointWalletRecord', true) && checkAction('admin.member.addPointWalletRecord')) ||
                 (get(props.pointFeatrue, 'usePointWalletRecord', true) && checkAction('admin.member.usePointWalletRecord')),
      record: get(props.pointFeatrue, 'findPointWalletRecord', true) || isExternalPoint.value,
      balance: get(props.pointFeatrue, 'findPointWalletBalance', false) && pageShopPoint.value.expirationType === 'perRecord',
    }))

    // => 刷新
    const refresh = async () => {
      loading.value = true
      await findShopPoint()
      showPage.value = true
      await nextTick()
      const promises = []
      if (showControl.value.record && recordListRef.value) {
        promises.push(recordListRef.value.refresh())
      }
      if (showControl.value.balance && balanceDetailRef.value) {
        promises.push(balanceDetailRef.value.refresh())
      }
      await Promise.all(promises)
      emit('updatePoint')
      loading.value = false
    }

    // => 取得點數資訊
    const findShopPoint = async () => {
      const [res, err] = await FindShopPoint({ shopId: shopId.value })
      if (err) return (window.$message.error(err))
      if (res.id) registered.value = true
      else loading.value = false
    }

    provide('pageShopPoint', pageShopPoint)

    onMounted(async () => {
      await refresh()
    })

    return {
      loading,
      showPage,
      registered,
      isExternalPoint,
      refresh,
      showControl,
      activeSubTab,
      subTabOptions,
      balanceDetailRef,
      recordListRef,
    }
  },
})

</script>

<style scoped lang="postcss">

</style>
